/**
 * Extension Connection Status Hook
 *
 * Provides real-time connection status for extensions with live polling
 */

import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'

export interface ExtensionConnectionStatus {
  isConnected: boolean
  isLoading: boolean
  error: string | null
  connectionUrl: string
  toolkitSlug: string
  accounts: Array<{
    id: string
    status: string
    created_at: string
    updated_at: string
  }>
  count: number
}

interface UseExtensionConnectionOptions {
  extensionId?: string
  toolkitSlug?: string
  pollInterval?: number // ms, default 3000
  autoStart?: boolean // default true
}

export function useExtensionConnection(options: UseExtensionConnectionOptions = {}) {
  const { extensionId, toolkitSlug, pollInterval = 3000, autoStart = true } = options

  const [status, setStatus] = useState<ExtensionConnectionStatus>({
    isConnected: false,
    isLoading: false,
    error: null,
    connectionUrl: '/extensions',
    toolkitSlug: toolkitSlug || '',
    accounts: [],
    count: 0
  })

  const [isPolling, setIsPolling] = useState(false)

  const checkConnectionStatus = useCallback(async () => {
    if (!toolkitSlug) return

    setStatus((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await fetch('/api/extensions/check-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          toolkitSlug,
          extensionId
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.success) {
        setStatus((prev) => ({
          ...prev,
          isConnected: data.connected,
          isLoading: false,
          error: null,
          connectionUrl: data.connection_url || '/extensions',
          toolkitSlug: data.toolkit_slug || toolkitSlug,
          accounts: data.accounts || [],
          count: data.count || 0
        }))
      } else {
        setStatus((prev) => ({
          ...prev,
          isConnected: false,
          isLoading: false,
          error: data.message || 'Connection check failed',
          accounts: [],
          count: 0
        }))
      }
    } catch (error) {
      console.error('Extension connection check failed:', error)
      setStatus((prev) => ({
        ...prev,
        isConnected: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Connection check failed',
        accounts: [],
        count: 0
      }))
    }
  }, [toolkitSlug, extensionId])

  // Start/stop polling
  const startPolling = useCallback(() => {
    if (!toolkitSlug || isPolling) return

    setIsPolling(true)
    checkConnectionStatus() // Initial check

    const interval = setInterval(() => {
      checkConnectionStatus()
    }, pollInterval)

    return () => {
      clearInterval(interval)
      setIsPolling(false)
    }
  }, [toolkitSlug, isPolling, checkConnectionStatus, pollInterval])

  const stopPolling = useCallback(() => {
    setIsPolling(false)
  }, [])

  // Auto-start polling if enabled
  useEffect(() => {
    if (autoStart && toolkitSlug) {
      const cleanup = startPolling()
      return cleanup
    }
  }, [autoStart, toolkitSlug, startPolling])

  // Manual connection trigger
  const initiateConnection = useCallback(async () => {
    if (!extensionId) {
      toast.error('Extension ID is required to initiate connection')
      return null
    }

    try {
      const response = await fetch('/api/extensions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          extensionId,
          extensionName: toolkitSlug,
          extensionType: 'composio',
          authMode: 'composio_link'
        })
      })

      const data = await response.json()

      if (response.ok && data.authUrl) {
        return data.authUrl
      } else {
        toast.error(data.error || 'Failed to initiate connection')
        return null
      }
    } catch (error) {
      console.error('Failed to initiate connection:', error)
      toast.error('Failed to initiate connection')
      return null
    }
  }, [extensionId, toolkitSlug])

  // Open connection popup
  const openConnectionPopup = useCallback(async () => {
    const authUrl = await initiateConnection()
    if (!authUrl) return

    const popup = window.open(
      authUrl,
      'extension-auth',
      'width=600,height=700,scrollbars=yes,resizable=yes'
    )

    if (!popup) {
      toast.error('Popup blocked. Please allow popups for this site.')
      return
    }

    // Monitor popup for completion
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed)
        // Refresh connection status after popup closes
        setTimeout(() => {
          checkConnectionStatus()
        }, 1000)
      }
    }, 1000)

    // Auto-close after 10 minutes
    setTimeout(
      () => {
        if (!popup.closed) {
          popup.close()
          clearInterval(checkClosed)
        }
      },
      10 * 60 * 1000
    )
  }, [initiateConnection, checkConnectionStatus])

  // Disconnect extension
  const disconnect = useCallback(async () => {
    if (!extensionId) {
      toast.error('Extension ID is required to disconnect')
      return false
    }

    try {
      const response = await fetch(`/api/extensions?id=${extensionId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        toast.success('Extension disconnected successfully')
        checkConnectionStatus() // Refresh status
        return true
      } else {
        const data = await response.json()
        toast.error(data.error || 'Failed to disconnect extension')
        return false
      }
    } catch (error) {
      console.error('Failed to disconnect extension:', error)
      toast.error('Failed to disconnect extension')
      return false
    }
  }, [extensionId, checkConnectionStatus])

  return {
    status,
    isPolling,
    checkConnectionStatus,
    startPolling,
    stopPolling,
    initiateConnection,
    openConnectionPopup,
    disconnect,
    refetch: checkConnectionStatus
  }
}
